/**
 * Internal CodeMirror helpers — sl-e0il.1.2
 *
 * Concrete EditorAdapter implementation built on top of a CodeMirror 6
 * `EditorView`. Shared between `<CodeEditor>` and `<TemplateEditor>`,
 * which both wrap `@uiw/react-codemirror` and expose the adapter via
 * `forwardRef` + `useImperativeHandle`. Keeping the adapter logic here
 * (not inline in each component) is the DRY seam.
 *
 * Also exports `useEchoSuppressedValue` — a race guard used by both
 * editors to filter out stale controlled-value echoes from the parent
 * while the user is mid-typing. See the hook's comment for the full
 * explanation of the controlled-input race we are defending against.
 */

import type { EditorView } from '@codemirror/view';
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  CodeEditorSelectionContext,
  CodeEditorSelectionMetadata,
  EditorAdapter,
} from '../editor-adapter';

/**
 * Build an `EditorAdapter` that reads + writes through a CodeMirror
 * `EditorView`. The `viewRef` is a MutableRefObject the caller updates
 * once the view becomes available (via `@uiw/react-codemirror`'s ref).
 *
 * All methods are no-ops when the view is not yet mounted — this
 * matches the lazy-load / suspense boundary where consumers may hold
 * an adapter ref before the chunk finishes loading.
 */
export function createCodeMirrorAdapter(viewRef: {
  readonly current: EditorView | null | undefined;
}): EditorAdapter {
  return {
    insertAtCursor(text) {
      const view = viewRef.current;
      if (!view) return;
      const sel = view.state.selection.main;
      view.dispatch({
        changes: { from: sel.from, to: sel.to, insert: text },
        selection: { anchor: sel.from + text.length },
      });
    },
    getValue() {
      const view = viewRef.current;
      return view ? view.state.doc.toString() : '';
    },
    setValue(value) {
      const view = viewRef.current;
      if (!view) return;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: value },
      });
    },
    focus() {
      viewRef.current?.focus();
    },
    getSelectionContext(metadata) {
      const view = viewRef.current;
      return view ? readCodeMirrorSelectionContext(view, metadata) : null;
    },
  };
}

export function readCodeMirrorSelectionContext(
  view: EditorView,
  metadata: CodeEditorSelectionMetadata
): CodeEditorSelectionContext | null {
  const selection = view.state.selection.main;
  if (selection.empty) return null;

  const from = Math.min(selection.from, selection.to);
  const to = Math.max(selection.from, selection.to);
  if (from === to) return null;

  const doc = view.state.doc;
  const selectedText = doc.sliceString(from, to);
  const fromLine = doc.lineAt(from);
  const toLine = doc.lineAt(Math.max(from, to - 1));
  const lineText = doc.sliceString(fromLine.from, toLine.to);

  return {
    ...metadata,
    id: [
      metadata.surface,
      metadata.language,
      fromLine.number,
      toLine.number,
      from,
      to,
    ].join(':'),
    selectedText,
    fullText: doc.toString(),
    lineText,
    from,
    to,
    fromLine: fromLine.number,
    toLine: toLine.number,
  };
}

// =============================================================================
// Race-guarded controlled value
// =============================================================================

/**
 * Guarded controlled-value hook for CodeMirror editors wrapped in a
 * panel that commits `onChange` through an async chain (Y.js document
 * mutation → subscriber → React re-render). Without a guard, fast
 * typing races against the echo and CodeMirror resets its own doc to
 * a stale value:
 *
 *   1. User types `a` → CM fires onChange('a') → parent commits to Y.js
 *   2. User types `b` → CM doc is `ab`, onChange('ab') → parent commits
 *   3. User types `c` → CM doc is `abc`, onChange('abc') → parent commits
 *   4. React re-renders with props.value='a' (first commit resolved)
 *      → @uiw/react-codemirror dispatches replace(0..3, 'a')
 *      → editor snaps back to 'a' and the user loses 'b', 'c'
 *   5. A few frames later props.value catches up to 'ab', 'abc' and
 *      the editor jitters through them.
 *
 * Guard: every value the user emits is logged in a seen-map with a
 * monotonic version. When a prop echo carries a value whose version
 * is older than our latest emission, we treat it as stale and suppress
 * it. A genuinely new external value (node switch, fullscreen dialog
 * mirror, undo) is not in the map and passes through normally.
 *
 * Returns `{ value, onChange }` ready to hand to `<CodeMirror>`. The
 * parent's `onChange` is still called for every user keystroke — this
 * hook only intercepts prop-to-editor flow.
 */
export function useEchoSuppressedValue(
  propValue: string,
  parentOnChange: (value: string) => void
): {
  readonly value: string;
  readonly onChange: (value: string) => void;
} {
  const [displayValue, setDisplayValue] = useState(propValue);
  const displayValueRef = useRef(propValue);
  const versionRef = useRef(0);
  const seenVersionsRef = useRef<Map<string, number>>(
    new Map([[propValue, 0]])
  );

  useEffect(() => {
    if (propValue === displayValueRef.current) {
      return; // already in sync (typical: our own latest emission echoing)
    }
    const seen = seenVersionsRef.current.get(propValue);
    if (seen !== undefined && seen < versionRef.current) {
      return; // stale echo — parent is lagging behind our latest emission
    }
    // Genuine external update (or echo of a value we haven't emitted).
    displayValueRef.current = propValue;
    setDisplayValue(propValue);
  }, [propValue]);

  const onChange = useCallback(
    (next: string) => {
      versionRef.current += 1;
      seenVersionsRef.current.set(next, versionRef.current);
      // Bound memory: keep the most recent ~32 emissions. A runaway
      // typist is still protected because the guard only needs to
      // recognise recent echoes, not the entire session history.
      if (seenVersionsRef.current.size > 64) {
        const entries = [...seenVersionsRef.current.entries()]
          .sort((a, b) => a[1] - b[1])
          .slice(-32);
        seenVersionsRef.current = new Map(entries);
      }
      displayValueRef.current = next;
      setDisplayValue(next);
      parentOnChange(next);
    },
    [parentOnChange]
  );

  return { value: displayValue, onChange };
}
